import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ShowCard from "../../components/cards/ShowCard";
import Button from "../../components/buttons/Button";
import TextInput from "../../components/formInputs/TextInput";
import { fetchCrmProducts, purchasePackage } from "../../redux/subscription";
import "../../components/styles/pricingPage.scss";

/**
 * PricingPage — Subscription Products from CRM Landing
 * Figma: 1.1 Onboarding (Desktop) (24654:5921)
 *
 * Fetches product_categories from CRM landing endpoint.
 * Products render as ShowCards (poster) with images from card_image_id.
 * Clicking a product → purchase modal (email + phone) → purchase API → redirect_url.
 */

const IMAGE_ACCESS_KEY = "WkVjNWNscFhORDBLCg==";
const CURRENCY_MAP = { 58: "NGN", 1: "USD", 2: "EUR", 3: "GBP" };

const getProductImageUrl = (imageId) => {
  if (!imageId) return null;
  return `/api/client/v1/global/images/${imageId}?accessKey=${IMAGE_ACCESS_KEY}`;
};

const formatProductPrice = (product) => {
  const priceOpt = product.price_options?.[0];
  if (!priceOpt) return "Free";

  const amount = parseFloat(priceOpt.activation_price || 0);
  const currency = CURRENCY_MAP[priceOpt.currency_id] || "NGN";
  const pkg = priceOpt.packages?.[0];
  const duration = pkg?.purchase_period_duration || "";
  const format = pkg?.purchase_period_format || "";

  const periodLabel = duration && format
    ? `/${duration} ${format.charAt(0) + format.slice(1).toLowerCase()}`
    : "";

  return amount ? `${currency} ${amount.toLocaleString()}${periodLabel}` : "Free";
};

const PricingPage = () => {
  const navigate = useNavigate();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [purchaseError, setPurchaseError] = useState("");
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);

  // Redux state
  const { productCategories, productsLoading, isLoading } = useSelector(
    (state) => state.subscription
  );

  // Fetch CRM product categories on mount
  useEffect(() => {
    fetchCrmProducts();
  }, []);

  // Pre-fill email/phone from localStorage
  useEffect(() => {
    const storedEmail = localStorage.getItem("mango_user_email") || "";
    if (storedEmail) setEmail(storedEmail);
    const storedPhone = localStorage.getItem("mango_user_phone") || "";
    if (storedPhone) setPhone(storedPhone);
  }, []);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setShowPurchaseForm(true);
    setPurchaseError("");
  };

  const handlePurchase = async () => {
    if (!email.trim()) {
      setPurchaseError("Please enter your email address");
      return;
    }
    if (!phone.trim()) {
      setPurchaseError("Please enter your phone number");
      return;
    }
    if (!selectedProduct) return;

    setPurchaseError("");

    // Default to the product's first price option uid (required in CRM payload).
    const priceOption = selectedProduct.price_options?.[0]?.uid || selectedProduct.price_option || "";
    const result = await purchasePackage(selectedProduct.id, email.trim(), phone.trim(), priceOption);

    if (result.success && result.redirectUrl) {
      window.location.href = result.redirectUrl;
    } else if (result.success) {
      setShowPurchaseForm(false);
      setSelectedProduct(null);
      navigate("/onboarding/register");
    } else {
      setPurchaseError(result.error || "Purchase failed. Please try again.");
    }
  };

  const handleClosePurchaseForm = () => {
    setShowPurchaseForm(false);
    setPurchaseError("");
  };

  const handleSkip = () => {
    navigate("/onboarding/register");
  };

  return (
    <div className="pricing-page">
      {/* Background pattern */}
      <div className="pricing-page__bg">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="pricing-grid" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M 80 0 L 0 0 0 80" fill="none" stroke="#1a1a1e" strokeWidth="0.5" />
            </pattern>
            <radialGradient id="glow" cx="35%" cy="40%" r="40%">
              <stop offset="0%" stopColor="#1a1a1e" stopOpacity="0.5" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#pricing-grid)" />
          <rect width="100%" height="100%" fill="url(#glow)" />
        </svg>
      </div>

      {/* Main Container */}
      <div className="pricing-page__container">
        {/* Title Section */}
        <div className="pricing-page__title-section">
          <div className="pricing-page__title-content">
            <span className="pricing-page__label">PRICING</span>
            <h1 className="pricing-page__title">
              Find the Perfect Plan for Your Movie Nights
            </h1>
            <p className="pricing-page__description">
              Experience high-quality content, from movies, series, to live
              sports and news broadcasts that you won't want to miss.
            </p>
          </div>
        </div>

        {/* Product Categories */}
        <div className="pricing-page__products">
          {productsLoading ? (
            <div className="pricing-page__loading">Loading plans...</div>
          ) : !productCategories || productCategories.length === 0 ? (
            <div className="pricing-page__loading">
              No subscription plans available.
            </div>
          ) : (
            productCategories.map((category) => (
              <div key={category.id || category.uid} className="pricing-page__category">
                <h3 className="pricing-page__category-title">{category.name}</h3>
                <div className="pricing-page__grid">
                  {(category.content || []).map((product) => (
                    <ShowCard
                      key={product.id || product.uid}
                      title={product.name}
                      image={getProductImageUrl(product.card_image_id)}
                      subtitle={formatProductPrice(product)}
                      variant="poster"
                      onClick={() => handleProductClick(product)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}

          <Button
            label="Skip for now"
            variant="ghost"
            size="lg"
            action={handleSkip}
          />
        </div>
      </div>

      {/* Purchase Form Modal */}
      {showPurchaseForm && selectedProduct && (
        <div className="pricing-page__overlay" onClick={handleClosePurchaseForm}>
          <div
            className="pricing-page__purchase-form"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pricing-page__form-product">
              <img
                src={getProductImageUrl(selectedProduct.card_image_id)}
                alt={selectedProduct.name}
                className="pricing-page__form-poster"
              />
              <div className="pricing-page__form-product-info">
                <h3 className="pricing-page__form-title">{selectedProduct.name}</h3>
                <p className="pricing-page__form-price">
                  {formatProductPrice(selectedProduct)}
                </p>
              </div>
            </div>

            <div className="pricing-page__form-fields">
              <TextInput
                label="Email Address"
                value={email}
                onChange={setEmail}
                placeholder="Enter your email"
                type="email"
              />
              <TextInput
                label="Phone Number"
                value={phone}
                onChange={setPhone}
                placeholder="Enter your phone number"
                type="tel"
              />
            </div>

            {purchaseError && (
              <p className="pricing-page__form-error">{purchaseError}</p>
            )}

            <div className="pricing-page__form-actions">
              <Button
                label={isLoading ? "Processing..." : "Proceed to Payment"}
                variant="primary"
                size="lg"
                isLoading={isLoading}
                action={handlePurchase}
              />
              <button
                className="pricing-page__form-cancel"
                onClick={handleClosePurchaseForm}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPage;
